import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { encodeSupervisorAuth, type SupervisorCredentials } from '@his/shared';
import { ApiError, get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, PasswordInput } from '@/components/ui/primitives';
import type { MyAuthority } from './accounts-types';

export const SUPERVISOR_HEADER = 'X-Supervisor-Auth';

export function supervisorHeaders(credentials: SupervisorCredentials | null): Record<string, string> | undefined {
  return credentials ? { [SUPERVISOR_HEADER]: encodeSupervisorAuth(credentials) } : undefined;
}

export function needsDiscountApproval(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.code === 'discount_needs_approval';
}

export function needsTotp(error: unknown): boolean {
  return error instanceof ApiError && error.details.some((detail) => detail.path === 'totpCode');
}

export function useMyAuthority() {
  const { user, hasModule } = useAuth();
  return useQuery({
    queryKey: ['my-authority', user?.id],
    queryFn: () => get<MyAuthority>('/accounts/authority/me'),
    enabled: Boolean(user && !user.isPlatformUser && (hasModule('billing') || hasModule('pharmacy') || hasModule('inventory'))),
    staleTime: 5 * 60_000,
  });
}

export function DiscountHint() {
  const mine = useMyAuthority();
  if (!mine.data) return null;
  if (mine.data.isSuperAdmin) return <span>{tr('billing', 'no_discount_limit_applies_to_you')}</span>;
  const pct = mine.data.values.find((value) => value.kind === 'discount_pct');
  const cap = mine.data.values.find((value) => value.kind === 'discount_max_minor');
  if (!pct || pct.dormant || (pct.value ?? 0) === 0) return <span>{tr('billing', 'you_cannot_give_a_discount_yourself_a')}</span>;
  const capText = cap && !cap.dormant && cap.value !== null ? `, at most ${money(cap.value)} on one bill` : '';
  return (
    <span>
      {tr('billing', 'up_to_2')} {pct.value}{tr('billing', 'without_approval')}{capText}{tr('billing', 'above_that_a_supervisor_approves_it')}
    </span>
  );
}

export function SupervisorDialog({
  open,
  title,
  message,
  onClose,
  onApprove,
  busy,
  error,
  alternative,
}: {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
  onApprove: (credentials: SupervisorCredentials) => void;
  busy?: boolean;
  error?: string | null;
  alternative?: { label: string; onClick: () => void };
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={tr('billing', 'the_supervisor_signs_in_here_their_password_is')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          {alternative && (
            <Button variant="outline" onClick={alternative.onClick}>
              {alternative.label}
            </Button>
          )}
          <Button
            onClick={() => onApprove({ username: username.trim(), password, ...(totpCode.trim() ? { totpCode: totpCode.trim() } : {}) })}
            loading={busy}
            disabled={!username.trim() || !password}
          >
            {tr('billing', 'approve')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">{message}</Alert>
        <Field label={tr('billing', 'supervisor_username')}>
          <Input autoComplete="off" value={username} onChange={(event) => setUsername(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'supervisor_password')}>
          <PasswordInput autoComplete="off" value={password} onChange={(event) => setPassword(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'authenticator_code')} hint={tr('billing', 'only_if_the_supervisor_uses_two_factor_sign_in')}>
          <Input inputMode="numeric" autoComplete="one-time-code" value={totpCode} onChange={(event) => setTotpCode(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
